import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Displays a "Showing X of Y" results counter with optional refreshing indicator.
 *
 * @param {Object} props - Component props
 * @param {number} props.loadedCount - Number of currently loaded items
 * @param {number} [props.totalCount] - Total number of items available
 * @param {boolean} [props.isRefreshing=false] - Whether data is being refreshed
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {JSX.Element|null} Results counter or null if no totalCount
 *
 * @example
 * <ResultsCounter loadedCount={50} totalCount={1523} isRefreshing={false} />
 * // Renders: "Showing 50 of 1,523"
 *
 * @example
 * <ResultsCounter loadedCount={50} totalCount={1523} isRefreshing={true} />
 * // Renders: "Showing 50 of 1,523 • Refreshing..."
 */
export function ResultsCounter({ loadedCount, totalCount, isRefreshing = false, className = '' }) {
    // Don't render if we don't have a total count
    if (totalCount === undefined || totalCount === null) {
        return null;
    }

    const formattedLoaded = loadedCount.toLocaleString();
    const formattedTotal = totalCount.toLocaleString();

    return (
        <div className={`flex items-center gap-2 text-sm text-muted-foreground ${className}`}>
            <span>
                Showing {formattedLoaded} of {formattedTotal}
            </span>
            {isRefreshing && (
                <span className="flex items-center gap-1">
                    <span>•</span>
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                    <span>Refreshing...</span>
                </span>
            )}
        </div>
    );
}

export default ResultsCounter;
